import { generatePath, matchPath } from 'react-router-dom';
import { isEmpty, getOr, pipe, toNumber, ary, isNil } from 'lodash/fp';
import { history } from 'app/history';
import { toStringFqn } from 'app/lib/utils';

export const Routes = {
    WEB_ROOT: '/',
    ADMIN: '/admin',
    SNOWFLAKE_MANAGEMENT: '/admin/snowflake',
    INTEGRATIONS_SLACK: '/admin/integrations-slack',
    INTEGRATIONS_GOOGLE: '/admin/integrations-google-sheets',
    INTEGRATIONS_OPENAI: '/admin/integrations-openai',
    DATASETS: '/datasets',
    PROJECTS: '/projects',
    PROJECT: '/projects/:projectSlug',
    VIEW: '/projects/:projectSlug/(view|data|sql|union|multiJoin|join|extractJson|notebook|schema|explore|query|filter)/:viewUid',
    VIEW_WORKBENCH: '/projects/:projectSlug/view/:viewUid?',
    VIEW_SQL_EDITOR: '/projects/:projectSlug/sql/:viewUid?',
    VIEW_NOTEBOOK: '/projects/:projectSlug/notebook/:viewUid',
    VIEW_UNION_EDITOR: '/projects/:projectSlug/union/:viewUid?',
    VIEW_JOIN_EDITOR: '/projects/:projectSlug/join/:viewUid?',
    VIEW_MULTI_JOIN_EDITOR: '/projects/:projectSlug/multiJoin/:viewUid?',
    VIEW_EXTRACT_JSON_EDITOR: '/projects/:projectSlug/extractJson/:viewUid?',
    VIEW_EXPLORE: '/projects/:projectSlug/explore/:viewUid?',
    VIEW_QUERY_BUILDER: '/projects/:projectSlug/query/:viewUid?',
    VIEW_FILTER: '/projects/:projectSlug/filter/:viewUid?',
    VIEW_MANAGE_COLUMNS: '/projects/:projectSlug/schema/:viewUid',
    DATASETS_EXPLORE: '/datasets/explore',
    GLOSSARY: '/glossary',
    DEPLOYMENT_OVERVIEW: '/deployments',
    JOB_MANAGEMENT: '/jobs',
    USAGE_REPORTING: '/usage',
    MIGRATION: '/migration',
    MIGRATION_WORKBENCH: '/migration-workbench/:uid',
    MODEL_JOBS: '/modelJobs/:modelId',
    INVENTORY: '/inventory'
};

const SLUG_REGEXP = /(\w+(?:-\w+)*)/;
const CELL_REGEXP = /cell-(\d+)-(\d+)/;

const getParamFromMatch = (param) => getOr(null, ['params', param]);
const match = (params) => (pathname) => matchPath(pathname || history.location.pathname, params);

export const isUsageReportingPage = pipe(match({ path: Routes.USAGE_REPORTING }), Boolean);

export const isJobManagementPage = pipe(match({ path: Routes.JOB_MANAGEMENT }), Boolean);

export const isMigrationPage = pipe(match({ path: Routes.MIGRATION }), Boolean);

export const isMigrationWorkbenchPage = pipe(match({ path: Routes.MIGRATION_WORKBENCH }), Boolean);

export const isProject = pipe(match({ path: Routes.PROJECT }), Boolean);

export const isGlossary = pipe(match({ path: Routes.GLOSSARY }), Boolean);

export const isWorkbench = pipe(match({ path: Routes.VIEW_WORKBENCH }), Boolean);

export const isSqlEditor = pipe(match({ path: Routes.VIEW_SQL_EDITOR }), Boolean);

const isUnionEditor = pipe(match({ path: Routes.VIEW_UNION_EDITOR }), Boolean);

const isJoinEditor = pipe(match({ path: Routes.VIEW_JOIN_EDITOR }), Boolean);

const isMultiJoinEditor = pipe(match({ path: Routes.VIEW_MULTI_JOIN_EDITOR }), Boolean);

const isExtractJsonEditor = pipe(match({ path: Routes.VIEW_EXTRACT_JSON_EDITOR }), Boolean);

const isManageColumns = pipe(match({ path: Routes.VIEW_MANAGE_COLUMNS }), Boolean);

const isFilterEditor = pipe(match({ path: Routes.VIEW_FILTER }), Boolean);

export const isView = () =>
    isWorkbench() ||
    isSqlEditor() ||
    isUnionEditor() ||
    isJoinEditor() ||
    isMultiJoinEditor() ||
    isExtractJsonEditor() ||
    isManageColumns() ||
    isFilterEditor();

export const isDatasetsList = pipe(match({ path: Routes.DATASETS }), Boolean);

const isProjects = pipe(match({ path: Routes.PROJECTS }), Boolean);
export const isProjectsList = (pathname) => isProjects(pathname) && !isProject(pathname);

export const isAdminPage = pipe(match({ path: Routes.ADMIN }), Boolean);

export const getCurrentProjectSlug = ary(
    0,
    pipe(match({ path: Routes.PROJECT }), getParamFromMatch('projectSlug'))
);

export const getCurrentViewUid = ary(
    0,
    pipe(match({ path: Routes.VIEW }), getParamFromMatch('viewUid'))
);

export const getCurrentCombinedSlug = () => {
    const projectSlug = getCurrentProjectSlug();
    const viewUid = getCurrentViewUid();

    return isNil(viewUid) ? null : `${projectSlug}/${viewUid}`;
};

export function navigate(path, options = {}, state = {}) {
    const { replace = false } = options;
    const method = replace ? 'replace' : 'push';
    history[method](path, { ...state, internal: true });
}

export function navigateToHome() {
    navigate(generatePath(Routes.DATASETS));
}

export function navigateToHomePageTable(fqn) {
    navigate(`${generatePath(Routes.DATASETS)}?fqn=${fqn}`);
}

export function navigateToProjects() {
    navigate(generatePath(Routes.PROJECTS));
}

export function navigateToJobManagementPage() {
    navigate(generatePath(Routes.JOB_MANAGEMENT));
}

export function generateJobDefinitionPath(uid) {
    return uid
        ? `${generatePath(Routes.JOB_MANAGEMENT)}/definitions?selected=${uid}`
        : `${generatePath(Routes.JOB_MANAGEMENT)}/definitions`;
}

export function generateJobDashboardPath(uid, period) {
    let path = `${generatePath(Routes.JOB_MANAGEMENT)}/dashboard?selected=${uid}`;

    if (period) {
        path += `&period=${period}`;
    }

    return path;
}

export function navigateToJobDefinitionPage(uid) {
    navigate(generateJobDefinitionPath(uid));
}

export function generateServiceAccountPath(uid) {
    return `${generatePath(Routes.JOB_MANAGEMENT)}/accounts?selected=${uid}`;
}

export function navigateToUsageReportingPage() {
    navigate(generatePath(Routes.USAGE_REPORTING));
}

export function navigateToMigrationPage() {
    navigate(generatePath(Routes.MIGRATION));
}

export function generateMigrationWorkbenchPath(uid) {
    return generatePath(Routes.MIGRATION_WORKBENCH, { uid });
}

export function navigateToMigrationWorkbench(uid) {
    navigate(generateMigrationWorkbenchPath(uid));
}

export function navigateToAdmin() {
    navigate(generatePath(Routes.ADMIN));
}

export function navigateToSnowflakeManagement() {
    navigate(generatePath(Routes.SNOWFLAKE_MANAGEMENT));
}

export function navigateToGoogleIntegration() {
    navigate(generatePath(Routes.INTEGRATIONS_GOOGLE));
}

export function navigateToOpenAIIntegration() {
    navigate(generatePath(Routes.INTEGRATIONS_OPENAI));
}

export function navigateToGlossary() {
    navigate(generatePath(Routes.GLOSSARY));
}

export function generateWorkbenchPath(projectSlug, viewUid) {
    return generatePath(Routes.VIEW_WORKBENCH, { projectSlug, viewUid });
}

export function navigateToWorkbench(projectSlug, viewUid, options, state) {
    navigate(generateWorkbenchPath(projectSlug, viewUid), options, state);
}

export function navigateToSqlEditor(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_SQL_EDITOR, { projectSlug, viewUid }));
}

export function navigateToNotebook(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_NOTEBOOK, { projectSlug, viewUid }));
}

export function navigateToUnionEditor(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_UNION_EDITOR, { projectSlug, viewUid }));
}

export function navigateToJoinEditor(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_JOIN_EDITOR, { projectSlug, viewUid }));
}

export function navigateToMultiJoinEditor(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_MULTI_JOIN_EDITOR, { projectSlug, viewUid }));
}

export function navigateToExtractJsonEditor(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_EXTRACT_JSON_EDITOR, { projectSlug, viewUid }));
}

export function navigateToManageColumns(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_MANAGE_COLUMNS, { projectSlug, viewUid }));
}

export function navigateToExplore(projectSlug, { queryUuid } = {}) {
    const params = new URLSearchParams();
    if (queryUuid) params.set('queryUuid', queryUuid);
    const search = params.toString();
    navigate(
        generatePath(Routes.VIEW_EXPLORE, { projectSlug, viewUid: getCurrentViewUid() }) +
            (search ? `?${search}` : '')
    );
}

export function navigateToQueryBuilder(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_QUERY_BUILDER, { projectSlug, viewUid }));
}

export function navigateToFilter(projectSlug, viewUid) {
    navigate(generatePath(Routes.VIEW_FILTER, { projectSlug, viewUid }));
}

export function navigateToDatasetExplore({ fqn, queryUuid } = {}) {
    const params = new URLSearchParams();
    if (fqn) params.set('fqn', toStringFqn(fqn));
    if (queryUuid) params.set('queryUuid', queryUuid);
    const search = params.toString();
    navigate(generatePath(Routes.DATASETS_EXPLORE) + (search ? `?${search}` : ''));
}

export function navigateToProjectSearchResults(term) {
    navigate(`${generatePath(Routes.PROJECTS)}?q=${term}`);
}

export function generateModelJobsManagementPath(modelId) {
    return generatePath(Routes.MODEL_JOBS, { modelId });
}

export function navigateToModelJobsManagementPage(modelId) {
    navigate(generateModelJobsManagementPath(modelId));
}

export function navigateToJobRun(jobRunId) {
    navigate(`${generatePath(Routes.JOB_MANAGEMENT)}/dashboard?selected=_jrun${jobRunId}`);
}

export function replaceHash(hash) {
    history.replace({ hash });
}

export function replaceProjectSlug(projectSlug) {
    history.replace({
        pathname: history.location.pathname.replace(
            new RegExp(`/projects/${SLUG_REGEXP.source}`),
            `/projects/${projectSlug}`
        )
    });
}

export function parseSelectedCell(location) {
    if (isEmpty(location.hash)) {
        return null;
    }

    const result = CELL_REGEXP.exec(location.hash);

    if (result) {
        const [, parsedColumnIndex, parsedRowIndex] = result;

        return {
            columnIndex: toNumber(parsedColumnIndex),
            rowIndex: toNumber(parsedRowIndex)
        };
    }
}

export function clearHistory() {
    history.replace('/');
}
